package fr.manyverse.app.infrastructure.image

import fr.manyverse.app.domain.error.ImageError
import fr.manyverse.app.domain.error.UnreadableImageError
import fr.manyverse.app.domain.image.ImageOptimizer
import fr.manyverse.app.domain.model.ImageData
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.jetbrains.skia.EncodedImageFormat
import org.jetbrains.skia.Image
import org.jetbrains.skia.Rect
import org.jetbrains.skia.Surface

private class SkikoImageOptimizer : ImageOptimizer {
    override suspend fun optimize(
        image: ImageData,
        maxDimension: Int,
        quality: Int
    ): Result<ImageData> = withContext(Dispatchers.Default) {
        runCatching {
            val isOptimalFormat = image.mimeType == "image/webp"

            // Skia lève une IllegalArgumentException quand il ne sait pas décoder le fichier
            val src = try {
                Image.makeFromEncoded(image.bytes)
            } catch (e: IllegalArgumentException) {
                throw UnreadableImageError(e)
            }
            val largestSide = maxOf(src.width, src.height)
            val needsResize = largestSide > maxDimension

            if (isOptimalFormat && !needsResize) return@runCatching image

            val scale = if (needsResize) maxDimension.toFloat() / largestSide.toFloat() else 1f
            val w = if (scale < 1f) (src.width * scale).toInt().coerceAtLeast(1) else src.width
            val h = if (scale < 1f) (src.height * scale).toInt().coerceAtLeast(1) else src.height

            val surface = Surface.makeRasterN32Premul(w, h)
            surface.canvas.drawImageRect(src, Rect.makeWH(w.toFloat(), h.toFloat()))
            val data = surface.makeImageSnapshot()
                .encodeToData(EncodedImageFormat.WEBP, quality)
                ?: throw ImageError("WebP encoding failed")

            val outFileName = if (image.mimeType == "image/webp") image.fileName else toWebpFileName(image.fileName)

            ImageData(bytes = data.bytes, mimeType = "image/webp", fileName = outFileName)
        }.classifyOptimizationFailure(image)
    }
}

actual fun createImageOptimizer(): ImageOptimizer = SkikoImageOptimizer()
